import React, {VoidFunctionComponent, useState, useEffect} from 'react';
import {useIntl} from 'react-intl';
import cx from 'classnames';

import {Work} from '../../../../../shared/types/work';

import IconButton from '../icon-button/icon-button';
import ArrowIcon, {ArrowIconDirection} from '../icons/arrow/arrow';
import FullscreenIcon, {
  FullscreenIconVariant
} from '../icons/fullscreen/fullscreen';

import styles from './photo-gallery.module.css';

interface Props {
  photos: Work['photos'];
  photoCredits: string;
}

const PhotoGallery: VoidFunctionComponent<Props> = props => {
  const {photos, photoCredits} = props;
  const {formatMessage} = useIntl();

  const [photoIndex, setPhotoIndex] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  // Reset gallery to first photo, when photos changed
  useEffect(() => {
    setPhotoIndex(0);
  }, [JSON.stringify(photos)]);

  const showPreviousPhoto = () => {
    setPhotoIndex(prevState => Math.max(0, prevState - 1));
  };

  const showNextPhoto = () => {
    setPhotoIndex(prevState => Math.min(photos.length - 1, prevState + 1));
  };

  const toggleFullscreen = () => {
    setIsFullscreen(prevState => !prevState);
  };

  if (!photos.length) {
    return null;
  }

  return (
    <div className={cx(styles.gallery, isFullscreen && styles.fullscreen)}>
      <figure className={styles.imageWrapper}>
        <img src={photos[photoIndex].url} />
        {photoCredits && (
          <figcaption>
            {formatMessage({id: 'work.photo'})}
            :&#32;{photoCredits}
          </figcaption>
        )}
      </figure>

      <div className={styles.navigation}>
        <IconButton
          className={styles.previousButton}
          variant="primary"
          onClick={showPreviousPhoto}
          disabled={photoIndex <= 0}>
          <ArrowIcon direction={ArrowIconDirection.LEFT} />
        </IconButton>
        <IconButton
          className={styles.nextButton}
          variant="primary"
          onClick={showNextPhoto}
          disabled={photoIndex >= photos.length - 1}>
          <ArrowIcon direction={ArrowIconDirection.RIGHT} />
        </IconButton>
        <IconButton
          className={styles.fullscreenButton}
          variant="primary"
          onClick={toggleFullscreen}>
          <FullscreenIcon
            variant={
              isFullscreen
                ? FullscreenIconVariant.MINIMIZE
                : FullscreenIconVariant.MAXIMIZE
            }
          />
        </IconButton>
      </div>
    </div>
  );
};

export default PhotoGallery;
